import type { DesignTokens } from "../types/theme";

/**
 * CashSync identity — "sophisticated financial confidence".
 *
 * Base: deep cool ink (not pure black) in dark / premium gray in light.
 * Accent: "money" emerald (#10b981 / teal-green) as brand color.
 * Financial semantics: income = emerald green, expense = coral red.
 * UI typography: Outfit (preloaded in index.html) with system stack.
 * Numerals: amounts use `tabular-nums` in UI to align columns.
 */
const UI_FONT =
  "'Outfit', ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif";

/**
 * Base tokens for Light mode.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f4f6f8",
  foreground: "#0e1a17",
  surface: "#ffffff",
  surfaceForeground: "#0e1a17",
  surfaceSecondary: "#eef1f3",
  surfaceSecondaryForeground: "#0e1a17",
  surfaceTertiary: "#e4e8eb",
  surfaceTertiaryForeground: "#0e1a17",
  overlay: "#ffffff",
  overlayForeground: "#0e1a17",
  muted: "#5b6b66",
  default: "#eef1f3",
  defaultForeground: "#0e1a17",
  accent: "#0a875a",
  accentForeground: "#ffffff",
  danger: "#e23d4f",
  dangerForeground: "#ffffff",
  warning: "#d98a0b",
  warningForeground: "#1a1305",
  success: "#0a875a",
  successForeground: "#ffffff",
  separator: "#cfd6da",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(14, 26, 23, 0.08)",
  glassHighlight: "rgba(14, 26, 23, 0.06)",
  glow0: "rgba(16, 185, 129, 0.14)",
  glow1: "rgba(20, 184, 166, 0.1)",
  glow2: "rgba(5, 150, 105, 0.08)",
  mutedForeground: "#5b6b66",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(14, 26, 23, 0.08)",
  overlayShadow: "rgba(14, 26, 23, 0.1)",
  fieldShadow: "rgba(14, 26, 23, 0.06)",
  fieldBorder: "rgba(14, 26, 23, 0.1)",
  fieldPlaceholder: "rgba(14, 26, 23, 0.4)",
  fieldBackground: "rgba(14, 26, 23, 0.03)",
  segment: "#ffffff",
  segmentForeground: "#0e1a17",
  link: "#0a875a",
  focus: "#10b981",
  fieldForeground: "#0e1a17",
  radius: "0.75rem",
  fontNormal: UI_FONT,
  fontMedium: UI_FONT,
  fontSemiBold: UI_FONT,
  fontBold: UI_FONT,
  border: "#d3dadd",
  primary: "#0a875a",
  primaryForeground: "#ffffff",
  secondary: "#eef1f3",
  secondaryForeground: "#0e1a17",
  backdrop: "rgba(8, 14, 12, 0.5)",
};

/**
 * Base tokens for Dark mode.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#0a0f0d",
  foreground: "#eef3f1",
  surface: "#131a18",
  surfaceForeground: "#eef3f1",
  surfaceSecondary: "#1a2320",
  surfaceSecondaryForeground: "#eef3f1",
  surfaceTertiary: "#22302b",
  surfaceTertiaryForeground: "#eef3f1",
  overlay: "#161e1b",
  overlayForeground: "#eef3f1",
  muted: "#8aa099",
  default: "#22302b",
  defaultForeground: "#eef3f1",
  accent: "#10b981",
  accentForeground: "#04140d",
  danger: "#f4546a",
  dangerForeground: "#1a0509",
  warning: "#f0a92e",
  warningForeground: "#1a1305",
  success: "#10b981",
  successForeground: "#04140d",
  separator: "#2c3a35",
  glassBackground: "rgba(13, 20, 17, 0.92)",
  glassBorder: "rgba(255, 255, 255, 0.1)",
  glassHighlight: "rgba(255, 255, 255, 0.22)",
  glow0: "rgba(16, 185, 129, 0.4)",
  glow1: "rgba(20, 184, 166, 0.32)",
  glow2: "rgba(5, 150, 105, 0.28)",
  mutedForeground: "#8aa099",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.4)",
  overlayShadow: "rgba(0, 0, 0, 0.5)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(255, 255, 255, 0.1)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.4)",
  fieldBackground: "rgba(255, 255, 255, 0.04)",
  segment: "#2c3a35",
  segmentForeground: "#eef3f1",
  link: "#34d399",
  focus: "#10b981",
  fieldForeground: "#eef3f1",
  radius: "0.75rem",
  fontNormal: UI_FONT,
  fontMedium: UI_FONT,
  fontSemiBold: UI_FONT,
  fontBold: UI_FONT,
  border: "#2c3a35",
  primary: "#10b981",
  primaryForeground: "#04140d",
  secondary: "#22302b",
  secondaryForeground: "#eef3f1",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
